/* =========================================================================
   aw-v2-product.js — comportements des sections de la fiche produit v2.
   Chargé en `defer`, uniquement sur le template product.

   Vanilla, sans dépendance. Chaque bloc s'initialise seul et ne fait rien si
   son markup est absent : les sections sont indépendantes et peuvent être
   retirées dans l'éditeur de thème sans casser les autres.
   ========================================================================= */
(function () {
  'use strict';

  /* ---------- Supports : onglets (desktop) / accordéon (mobile) ----------
     UN SEUL markup pour les deux, le CSS décide de l'apparence — mais le
     COMPORTEMENT doit suivre, et c'est précisément ce qui manquait. `activer()`
     posait `hidden` sur tous les panneaux sauf un, quelle que soit la largeur.
     En mobile, taper sur « Verre acrylique » ouvrait donc bien le <details>…
     sur un corps resté `hidden` : rien n'apparaissait. Seul le premier panneau
     s'affichait, parce qu'`activer(0)` venait de le démasquer. Le commentaire
     promettait de « laisser l'accordéon natif faire son travail » en dessous du
     point de rupture ; aucun code ne le faisait.

     Les deux motifs ARIA ne peuvent pas cohabiter sur le même DOM :
     — au-dessus du point de rupture, motif « tabs » : rôles tab/tabpanel, la
       visibilité vient de `hidden`, et les <details> sont forcés ouverts pour
       que le repli natif ne concurrence pas la sélection d'onglet ;
     — en dessous, motif « disclosure » : <details> natif, plus aucun `hidden`,
       et les rôles d'onglet sont retirés — laisser un aria-labelledby pointer
       vers un bouton en display:none est pire que pas d'étiquette du tout.

     La bascule est rejouée à chaque franchissement du point de rupture. Un
     matchMedia lu une seule fois au chargement laisserait la section dans le
     mode de la largeur de départ après une rotation ou un redimensionnement.

     Limite connue : sans JavaScript, les panneaux restent `hidden` (l'attribut
     vient du Liquid, pour éviter que les quatre s'affichent en bloc le temps
     que le script arrive). Le repli sans JS est un chantier distinct.       */
  function supports(racine) {
    var onglets  = Array.prototype.slice.call(racine.querySelectorAll('[role="tab"]'));
    var panneaux = Array.prototype.slice.call(racine.querySelectorAll('.awv2-supports__corps'));
    var details  = Array.prototype.slice.call(racine.querySelectorAll('.awv2-supports__details'));
    if (!onglets.length || !panneaux.length) return;

    var mq = window.matchMedia('(min-width: 750px)');
    var etiquettes = panneaux.map(function (p) { return p.getAttribute('aria-labelledby'); });
    var actif = 0;

    function activer(i, donnerLeFocus) {
      actif = i;
      onglets.forEach(function (o, k) {
        var on = k === i;
        o.setAttribute('aria-selected', on ? 'true' : 'false');
        o.tabIndex = on ? 0 : -1;
        o.classList.toggle('is-actif', on);
      });
      panneaux.forEach(function (p, k) {
        p.hidden = k !== i;
        p.classList.toggle('is-actif', k === i);
      });
      if (donnerLeFocus) onglets[i].focus();
    }

    function refleterAccordeon() {
      details.forEach(function (d) {
        var resume = d.querySelector('.awv2-supports__resume');
        if (resume) resume.setAttribute('aria-expanded', d.open ? 'true' : 'false');
      });
    }

    function appliquerMode() {
      if (mq.matches) {
        details.forEach(function (d) {
          d.open = true;
          var resume = d.querySelector('.awv2-supports__resume');
          if (resume) resume.removeAttribute('aria-expanded');
        });
        panneaux.forEach(function (p, k) {
          p.setAttribute('role', 'tabpanel');
          p.setAttribute('tabindex', '0');
          if (etiquettes[k]) p.setAttribute('aria-labelledby', etiquettes[k]);
        });
        activer(actif, false);
      } else {
        panneaux.forEach(function (p) {
          p.hidden = false;
          p.classList.remove('is-actif');
          p.removeAttribute('role');
          p.removeAttribute('tabindex');
          p.removeAttribute('aria-labelledby');
        });
        details.forEach(function (d, k) { d.open = k === actif; });
        refleterAccordeon();
      }
    }

    onglets.forEach(function (o, i) {
      o.addEventListener('click', function () { activer(i, false); });
      o.addEventListener('keydown', function (e) {
        var n = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = (i + 1) % onglets.length;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = (i - 1 + onglets.length) % onglets.length;
        if (e.key === 'Home') n = 0;
        if (e.key === 'End') n = onglets.length - 1;
        if (n === null) return;
        e.preventDefault();
        activer(n, true);
      });
    });

    /* Le panneau déplié en mobile devient l'onglet sélectionné si l'on repasse
       en desktop, et réciproquement : on ne perd pas sa lecture en tournant le
       téléphone. */
    details.forEach(function (d, i) {
      d.addEventListener('toggle', function () {
        if (mq.matches) return;
        if (d.open) actif = i;
        refleterAccordeon();
      });
    });

    if (mq.addEventListener) {
      mq.addEventListener('change', appliquerMode);
    } else if (mq.addListener) {
      mq.addListener(appliquerMode);          // Safari < 14
    }

    appliquerMode();
  }

  /* ---------- Atelier : vidéo autonome ----------
     Lecture quand la vidéo est visible à moitié, pause sinon : on ne fait pas
     tourner un décodeur pour un élément hors écran. Le son est coupé au départ
     — une vidéo qui démarre avec du son est une nuisance, et les navigateurs
     refusent de toute façon la lecture automatique sonore.                 */
  function atelier(racine) {
    var video = racine.querySelector('video');
    if (!video) return;

    if ('IntersectionObserver' in window) {
      var obs = new IntersectionObserver(function (entrees) {
        entrees.forEach(function (e) {
          if (e.isIntersecting) {
            var p = video.play();
            if (p && p.catch) p.catch(function () { /* lecture refusée : sans conséquence */ });
          } else {
            video.pause();
          }
        });
      }, { threshold: 0.5 });
      obs.observe(video);
    }

    var bouton = racine.querySelector('[data-awv2-son]');
    if (!bouton) return;
    function refleter() {
      var coupe = video.muted;
      bouton.setAttribute('aria-pressed', coupe ? 'false' : 'true');
      bouton.setAttribute('aria-label', coupe ? 'Activer le son' : 'Couper le son');
      bouton.classList.toggle('is-actif', !coupe);
    }
    bouton.addEventListener('click', function () {
      video.muted = !video.muted;
      if (!video.muted) {
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      }
      refleter();
    });
    refleter();
  }

  /* ---------- Sélection : rail horizontal et flèches ----------
     app.js (bundle prakt) remplace le contenu de .product-recommendations dès que
     les recommandations arrivent : le rail et les flèches présents au chargement
     ne sont pas ceux que l'internaute finira par manipuler. Tout passe donc par
     délégation depuis la racine, seule à survivre au remplacement, et un
     observateur remet l'état des flèches à jour après coup.                  */
  function selection(racine) {
    if (racine.dataset.awv2Pret) return;
    racine.dataset.awv2Pret = '1';

    function rail() { return racine.querySelector('[data-awv2-rail]'); }

    /* Un cran = la distance d'une carte à la suivante, gouttière comprise. On la
       mesure plutôt que de la coder en dur : elle change au point de rupture. */
    function cran(r) {
      var cartes = r.children;
      if (cartes.length > 1) return cartes[1].offsetLeft - cartes[0].offsetLeft;
      return cartes.length ? cartes[0].offsetWidth : r.clientWidth;
    }

    function maj() {
      var r = rail();
      if (!r) return;
      var course = r.scrollWidth - r.clientWidth;
      racine.querySelectorAll('[data-awv2-fleche]').forEach(function (bouton) {
        var sens = Number(bouton.dataset.awv2Fleche);
        var bout = sens < 0 ? r.scrollLeft <= 1 : r.scrollLeft >= course - 1;
        // Rien à faire défiler : les deux flèches s'éteignent.
        bouton.disabled = course <= 1 || bout;
      });
    }

    racine.addEventListener('click', function (e) {
      var bouton = e.target.closest ? e.target.closest('[data-awv2-fleche]') : null;
      if (!bouton || bouton.disabled) return;
      var r = rail();
      if (r) r.scrollBy({ left: Number(bouton.dataset.awv2Fleche) * cran(r), behavior: 'smooth' });
    });

    /* `scroll` ne remonte pas : l'écouter en phase de capture est le seul moyen
       de le voir passer depuis un ancêtre resté stable. */
    racine.addEventListener('scroll', maj, true);
    window.addEventListener('resize', maj);

    if ('MutationObserver' in window) {
      var zone = racine.querySelector('.product-recommendations');
      if (zone) new MutationObserver(maj).observe(zone, { childList: true });
    }

    maj();
  }

  function demarrer() {
    document.querySelectorAll('[data-awv2="supports"]').forEach(supports);
    document.querySelectorAll('[data-awv2="atelier"]').forEach(atelier);
    document.querySelectorAll('[data-awv2="selection"]').forEach(selection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }

  /* L'éditeur de thème réinjecte le markup d'une section sans recharger la
     page : sans ces écoutes, une section rééditée perdrait ses comportements. */
  document.addEventListener('shopify:section:load', demarrer);
  document.addEventListener('shopify:section:unload', function (e) {
    var v = e.target.querySelector('video');
    if (v) v.pause();
  });
})();
